import Vue from 'vue'
import { RawLocation } from 'vue-router'
import { mapGetters, mapActions } from 'vuex'
import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import moment from 'moment'
import { AxiosPromise, AxiosResponse } from 'axios'
import Icon from '@/components/Icon/Icon.vue'
import PaidLabel from '@/components/PaidLabel/PaidLabel.vue'
import NumberColumn from '@/components/Columns/NumberColumn.vue'
import DurationColumn from '@/components/Columns/DurationColumn.vue'
import { SCRIPTS__MAKE_ACTION } from '@/store/actions'
import {
  ScriptColumnInitObject,
  ScriptUnit,
  ScriptVersion,
  ScriptActionPayload,
  CreateDraftPayload,
  CreateDraftResponse,
} from '@/models/scripts'
import { UserScope } from '@/models/users'
import { ScriptAction, ScriptPublishStatusMessages } from '@/constants/scripts'
import { GridColumn } from '@/@types'
import { RouteNames } from '@/router'

interface Methods {
  [SCRIPTS__MAKE_ACTION](data: ScriptActionPayload): AxiosPromise
  [ScriptAction.Draft]: (
    payload: CreateDraftPayload,
  ) => Promise<CreateDraftResponse>
  getComponentForThis: (column: GridColumn) => string
  getInitObjectForThis: (column: GridColumn) => ScriptColumnInitObject
  versionAction: () => void
  handleTogglePublish: () => void
  clone: () => void
  createDraft: () => void
  removeDraft: () => void
}

interface Computed {
  getScriptColumns: GridColumn[]
  isDraft: boolean
  statColumns: GridColumn[]
  columnsWidth: string
  actionText: string
  actionIcon: string
  actionColor: string
  actionTooltipColor: string
  date: string
  name: string
  versionClass: string
  editRoute: RawLocation
  createVersionRoute: RawLocation
  previewVersionRoute: RawLocation
  canCloneScript: boolean
  canDoScope: (checkingScope: UserScope) => boolean
}

interface Props {
  item: ScriptUnit
  version: ScriptVersion
  hasDraft: boolean
  hasActive: boolean
}

export default Vue.extend<{}, Methods, Computed, Props>({
  components: {
    Icon,
    PaidLabel,
    NumberColumn,
    DurationColumn,
  },
  props: {
    item: {
      required: true,
    },
    version: {
      required: true,
    },
    hasDraft: {
      required: true,
    },
    hasActive: {
      required: true,
    },
  },
  computed: {
    ...mapGetters(['getScriptColumns', 'canDoScope']),
    isDraft(): boolean {
      return !this.version.instance.date
    },
    canCloneScript(): boolean {
      return !this.isDraft && this.canDoScope(UserScope.ScriptsEdit)
    },
    statColumns(): GridColumn[] {
      return this.getScriptColumns.filter((one: GridColumn) =>
        ['steps', 'pass', 'target', 'conversion', 'duration'].includes(
          one.name,
        ),
      )
    },
    columnsWidth(): string {
      return [
        '1fr',
        ...this.statColumns.map((one: GridColumn) => one.width),
        '14.75rem',
      ].join(' ')
    },
    actionText(): string {
      return this.$tc(
        this.version.active ? 'script.actions.unpublish' : 'common.publish',
      )
    },
    actionIcon(): string {
      return this.version.active ? 'zmdi-layers-off' : 'zmdi-layers'
    },
    actionColor(): string {
      return this.version.active ? 'red' : 'green'
    },
    actionTooltipColor(): string {
      return this.version.active ? '-danger' : '-green'
    },
    date(): string {
      const original = this.version.instance.date
      const unixDate = moment.unix(original)
      const dateValue = unixDate.clone().format('DD.MM.yyyy')
      const timeValue = unixDate.clone().format('HH:mm')
      return `${dateValue} ${timeValue}`
    },
    name(): string {
      if (this.isDraft) {
        return this.$tc('script.draft')
      }
      const name = this.version.instance.name || ''
      return `${this.date} ${name}`
    },
    versionClass(): string {
      if (this.isDraft) {
        return '-draft'
      }
      if (this.version.active) {
        return '-active'
      }
      return ''
    },
    editRoute(): RawLocation {
      return {
        name: RouteNames.ScriptSchemeEdit,
        params: {
          scriptId: this.item.id,
          instanceId: this.version.instance.id,
        },
      }
    },
    createVersionRoute(): RawLocation {
      return {
        name: RouteNames.VersionCreate,
        params: {
          scriptId: this.item.id,
        },
      }
    },
    previewVersionRoute(): RawLocation {
      return {
        name: RouteNames.ScriptPage,
        params: {
          scriptId: this.item.id,
          instanceId: this.version.instance.id,
        },
      }
    },
  },
  methods: {
    ...mapActions([SCRIPTS__MAKE_ACTION, ScriptAction.Draft]),
    getComponentForThis(column: GridColumn): string {
      return column.type + 'Column'
    },
    getInitObjectForThis(column: GridColumn): ScriptColumnInitObject {
      const init: ScriptColumnInitObject = {
        id: this.item.id,
        title: this.item.title,
        value: null,
      }
      const typeStats = this.version.instance
      switch (column.name) {
        case 'steps':
        case 'pass':
        case 'target':
        case 'duration':
          init.value = typeStats[column.name]
          break
        case 'conversion':
          init.target = typeStats.target
          init.pass = typeStats.pass
          if (
            typeof init.target === 'number' &&
            typeof init.pass === 'number' &&
            init.pass > 0
          ) {
            init.value =
              new Intl.NumberFormat('ru-RU', {
                maximumFractionDigits: 2,
              }).format((init.target / init.pass) * 100) + '%'
          } else {
            init.value = '0%'
          }
      }
      return init
    },
    clone(): void {
      if (!this.canCloneScript) {
        return
      }
      this[SCRIPTS__MAKE_ACTION]({
        action: ScriptAction.Clone,
        id: this.item.id,
        instance: this.version.instance.id,
      }).then(response => {
        if (response.data) {
          this.$store.dispatch(ADD_TOAST_MESSAGE, {
            text: this.$tc('script.actions.copySuccess'),
            type: 'info',
          })
        }
      })
    },
    createDraft() {
      const payload: CreateDraftPayload = {
        id: this.item.id,
        instance: this.version.instance.id,
      }
      this[ScriptAction.Draft](payload)
    },
    removeDraft(): void {
      this.$useConfirmDialog(
        [
          this.$tc('common.areYouSure'),
          {
            type: 'danger',
            okText: this.$tc('common.remove'),
            cancelText: this.$tc('common.cancel'),
          },
        ],
        () => {
          this[SCRIPTS__MAKE_ACTION]({
            action: ScriptAction.RemoveDraft,
            id: this.item.id,
          }).then(response => {
            if (response.data) {
              this.$store.dispatch(ADD_TOAST_MESSAGE, {
                text: this.$tc('script.draftRemoved'),
                type: 'info',
              })
            }
          })
        },
        this.isDraft,
      )
    },
    versionAction(): void {
      if (!this.hasActive) {
        return this.handleTogglePublish()
      }

      const confirmText = this.version.active
        ? this.$tc('script.actions.unpublushVersion')
        : this.$tc('script.actions.changeVersion')

      this.$useConfirmDialog(confirmText, () => {
        this.handleTogglePublish()
      })
    },
    handleTogglePublish(): void {
      const action = this.version.active
        ? ScriptAction.UnPublish
        : ScriptAction.Publish

      this[SCRIPTS__MAKE_ACTION]({
        action,
        id: this.item.id,
        instance: this.version.instance.id,
      }).then((response: AxiosResponse<{ publish: number } | boolean>) => {
        let text = this.$tc('script.actions.unpublishSuccess')
        if (
          action === ScriptAction.Publish &&
          typeof response.data === 'object' &&
          response.data.publish
        ) {
          text = ScriptPublishStatusMessages[response.data.publish]
        }
        this.$store.dispatch(ADD_TOAST_MESSAGE, {
          text,
          type: 'info',
        })
      })
    },
  },
})
